/**
 * <ThemePreferenceSync /> — bidirectional sync of `theme` between the
 * ThemeProvider (localStorage-backed) and the server-side
 * `/api/user/preferences.theme` blob.
 *
 * Rendered once (inside the NotificationCenterProvider so it has access to
 * the same fetch path) and produces no UI. Behavior:
 *   - On first mount for an authenticated user: GET /api/user/preferences.
 *     If `preferences.theme` is set AND differs from the local theme, adopt
 *     the server value (cross-device sync wins on initial load).
 *   - On subsequent `theme` changes: PUT the new value back to the server
 *     in a fire-and-forget fashion (UI never blocks on the network).
 *
 * This component is best-effort — network failures fall back to localStorage
 * silently, identical to how the ThemeProvider has always behaved.
 */
import { useEffect, useRef } from 'react';

import { useAuth } from '../context/AuthContext';
import { useTheme } from './ui/ThemeToggle';
import { userPreferencesAPI } from '../lib/api';

export const ThemePreferenceSync = () => {
  const { isAuthenticated, user } = useAuth();
  const { theme, setTheme } = useTheme();
  const hydratedForUserRef = useRef(null);

  // Adopt the server's value on first mount per session.
  useEffect(() => {
    if (!isAuthenticated || !user?.user_id) return;
    if (hydratedForUserRef.current === user.user_id) return;
    hydratedForUserRef.current = user.user_id;

    userPreferencesAPI
      .get()
      .then((r) => {
        const serverTheme = r?.data?.preferences?.theme;
        if (
          serverTheme &&
          (serverTheme === 'dark' || serverTheme === 'light') &&
          serverTheme !== theme
        ) {
          setTheme(serverTheme);
        }
      })
      .catch(() => {
        // Best-effort; stick with local theme.
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isAuthenticated, user?.user_id]);

  // After hydration, mirror local theme changes back to the server.
  useEffect(() => {
    if (!isAuthenticated || !user?.user_id) return;
    if (hydratedForUserRef.current !== user.user_id) return;

    // Re-read existing server prefs to avoid clobbering toast_mute etc.
    userPreferencesAPI
      .get()
      .then((r) => {
        const current = r?.data?.preferences || {};
        if (current.theme === theme) return; // already in sync
        return userPreferencesAPI.put({ ...current, theme });
      })
      .catch(() => {
        /* silent best-effort */
      });
  }, [theme, isAuthenticated, user?.user_id]);

  return null;
};

export default ThemePreferenceSync;
